import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useCombinedRefs } from '../../ref';
import { useShallowMemo } from '../util';

const DEFAULT_ANCHOR = 'north';

const DEFAULT_POSITION = 'outside';

export type MagnetAnchor =
  | 'east-bottom'
  | 'east-top'
  | 'east'
  | 'north-east'
  | 'north-left'
  | 'north-right'
  | 'north-west'
  | 'north'
  | 'south-east'
  | 'south-left'
  | 'south-right'
  | 'south-west'
  | 'south'
  | 'west-bottom'
  | 'west-top'
  | 'west';

export type MagnetPosition = 'inside' | 'outside';

export interface UseMagnetOptions<T extends Element> {
  ref?: React.Ref<any> | React.MutableRefObject<T>;
  position?: MagnetPosition;
  anchor?: MagnetAnchor;
}

const computeOutsideTranslate = (
  anchor: MagnetAnchor,
): [string | number, string | number] => {
  switch (anchor) {
    case 'east-bottom':
    case 'east-top':
      return ['100%', 0];

    case 'east':
      return ['100%', '-50%'];

    case 'north-east':
      return ['100%', '-100%'];

    case 'north-left':
    case 'north-right':
      return [0, '-100%'];

    case 'north-west':
      return ['-100%', '-100%'];

    case 'north':
      return ['-50%', '-100%'];

    case 'south-east':
      return ['100%', '100%'];

    case 'south-left':
    case 'south-right':
      return [0, '100%'];

    case 'south-west':
      return ['-100%', '100%'];

    case 'south':
      return ['-50%', '100%'];

    case 'west-bottom':
    case 'west-top':
      return ['-100%', 0];

    case 'west':
      return ['-100%', '-50%'];

    default:
      return [0, 0];
  }
};

const computeInsideTranslate = (
  anchor: MagnetAnchor,
): [string | number, string | number] => {
  switch (anchor) {
    case 'north':
    case 'south':
      return ['-50%', 0];

    case 'west':
    case 'east':
      return [0, '-50%'];

    default:
      return [0, 0];
  }
};

const computeTranslate = (
  position: MagnetPosition,
  anchor: MagnetAnchor,
): (string | number)[] => {
  if (position === 'inside') {
    return computeInsideTranslate(anchor);
  } else {
    return computeOutsideTranslate(anchor);
  }
};

const computePosition = (
  anchor: MagnetAnchor,
): {
  top?: number | string;
  left?: number | string;
  right?: number | string;
  bottom?: number | string;
} => {
  switch (anchor) {
    case 'east-bottom':
    case 'south-east':
    case 'south-right':
      return { right: 0, bottom: 0 };

    case 'north-right':
    case 'north-east':
    case 'east-top':
      return { right: 0, top: 0 };

    case 'south-west':
    case 'west-bottom':
    case 'south-left':
      return { left: 0, bottom: 0 };

    case 'north-west':
    case 'north-left':
    case 'west-top':
      return { top: 0, left: 0 };

    case 'east':
      return { top: '50%', right: 0 };

    case 'north':
      return { top: 0, left: '50%' };

    case 'south':
      return { bottom: 0, left: '50%' };

    case 'west':
      return { top: '50%', left: 0 };
  }
  return {};
};

export const useMagnet = <T extends Element>(
  options: UseMagnetOptions<T> = {},
): [React.Ref<any>, React.CSSProperties] => {
  const { anchor = DEFAULT_ANCHOR, position = DEFAULT_POSITION } = options;

  const ref = useRef<Node | null>();
  const [style, setStyle] = useState<React.CSSProperties>({});

  const updateStyle = useCallback((): void => {
    setStyle({
      transform: `translate(${computeTranslate(position, anchor).join(',')})`,
      position: 'absolute',
      ...computePosition(anchor),
    });
  }, [setStyle, options.anchor]);

  useEffect(updateStyle, [options.anchor]);

  const setRef = useCallback((node: Node | null): void => {
    ref.current = node;
    if (node) {
      updateStyle();
    }
  }, []);

  return [setRef, style];
};
